import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Avatar,
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import { GetUrlParams, MinuteFormat, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/GalleryForm";

const Component = ({ onSetRoute, parentName, routeName }) => {
	const initialState = { data: [], pagination: {} };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "title",
			render: (value, record) => (
				<>
					{" "}
					{/* <Avatar src={record?.file} /> */}
					<span style={{ marginRight: "12px" }}>{value}</span>
				</>
			),
		},
		{
			title: Resources.description,
			dataIndex: "description",
			render: (value) => value?.slice(0, 90),
		},
		{
			title: Resources.section,
			dataIndex: "section",
			render: (value) => {
				switch (value) {
					case "user":
						return Resources.user;
					case "category":
						return Resources.category;
					case "course":
						return Resources.course;
					case "group":
						return Resources.group;
					case "package":
						return Resources.package;
					case "session":
						return Resources.session;
					case "banner":
						return Resources.banner;
					case "sample":
						return Resources.sample;
					case "platform":
						return Resources.platform;
					case "default":
						return Resources.default;
					default:
						return "نامشخص";
				}
			},
		},
		{
			title: Resources.extension,
			dataIndex: "extension",
			className: "center-cell ltr-cell",
			width: "80px",
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "120px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					<Tooltip title={Resources.edit}>
						<Button
							type="link"
							icon={<i className="fa fa-pencil-alt" />}
							onClick={() => handleForm(record)}
						/>
					</Tooltip>

					<Tooltip title={Resources.delete}>
						<Button
							type="link"
							icon={<i className="fa fa-trash-alt" />}
							onClick={() => handleDelete(value)}
						/>
					</Tooltip>
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, loading, pagination, selected } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.gallery
			.list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (id) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.gallery
					.delete(id)
					.then(() => {
						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const _search = ObjectToURL({ ...params, page: current });

		navigate({ pathname, search: _search });
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate({ pathname, search });
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={`${Resources.image}${Resources.ha}`}
				extra={
					<Button
						type="primary"
						onClick={() => handleForm()}
						loading={loading}
					>
						{Resources.add}
					</Button>
				}
			/>

			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="section">
							<Select
								options={[
									{ label: Resources.user, value: "user" },
									{ label: Resources.category, value: "category" },
									{ label: Resources.course, value: "course" },
									{ label: Resources.package, value: "package" },
									{ label: Resources.session, value: "session" },
									{ label: Resources.banner, value: "banner" },
									{ label: Resources.sample, value: "sample" },
									{ label: Resources.platform, value: "platform" },
									{ label: Resources.default, value: "default" },
								]}
								onChange={handleFilter}
								placeholder={Resources.section}
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 800 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
